import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  WHATSAPP_GET_LOADING,
  WHATSAPP_SYNC_LOADING,
} from "../../constants/loading.constant";
import { useGlobalCore } from "../globals";
import { whatsappApiService } from "./services/api";
import { settingSelector } from "./store/selector";
import {
  postUnbindWhatsappInitAction,
  postUnbindWhatsappSuccessAction,
  postUnbindWhatsappFailureAction,
  postBindWhatsappInitAction,
  postBindWhatsappSuccessAction,
  getWhatsappInitAction,
  getWhatsappSuccessAction,
  getWhatsappFailureAction,
} from "./store/api-actions";
import { toast } from "react-toastify";
import { WhatsAppConnectionMode } from "./config";
import { getWhatsAppSignupSession } from "./services/whatsapp-auth0-config";
import { WhatsAppConnectionType } from "./interface/api/post-whatsapp-bind";

export function useWhatsappCore() {
  const dispatch = useDispatch();
  const settings = useSelector(settingSelector);
  const { turnOnLoading, turnOffLoading } = useGlobalCore();

  const unbind = useCallback(
    async (companyId: string, onSuccess?: () => void) => {
      try {
        dispatch(postUnbindWhatsappInitAction());
        await whatsappApiService.unbind(companyId);
        dispatch(postUnbindWhatsappSuccessAction());
        onSuccess?.();
      } catch (error: any) {
        console.error(error);
        dispatch(postUnbindWhatsappFailureAction({ error }));
      }
    },
    [dispatch]
  );

  const bind = useCallback(
    async (
      companyId: string,
      token: string,
      phoneNumber: string,
      options: {
        pin?: number | null;
        connectionType?: WhatsAppConnectionType;
        wabaId?: string;
        displayPhoneNumber?: string;
      } = {},
      onSuccess?: () => void
    ) => {
      try {
        dispatch(postBindWhatsappInitAction());
        turnOnLoading(WHATSAPP_SYNC_LOADING);
        const response = await whatsappApiService.bind(companyId, {
          companyId,
          phoneNumber,
          token,
          pin: options.pin ?? undefined,
          connectionType: options.connectionType || "api_only",
          wabaId: options.wabaId,
          displayPhoneNumber: options.displayPhoneNumber,
        });
        dispatch(postBindWhatsappSuccessAction({ settings: response }));
        turnOffLoading(WHATSAPP_SYNC_LOADING, 500);
        onSuccess?.();
      } catch (error: any) {
        turnOffLoading(WHATSAPP_SYNC_LOADING);
        console.error(error);
        dispatch(postUnbindWhatsappFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading]
  );

  const completeAuth0Step = useCallback(
    async (companyId: string, accessToken: string, mode: WhatsAppConnectionMode) => {
      try {
        const connectionType: WhatsAppConnectionType =
          mode === "coexistence" ? "coexistence" : "api_only";
        const session = getWhatsAppSignupSession();

        const result = await whatsappApiService.auth0(companyId, accessToken, {
          connectionType,
          phoneNumberId: session.phoneNumberId,
          wabaId: session.wabaId,
        });

        await bind(
          companyId,
          result.access_token,
          result.phoneData.phoneNumberId,
          {
            pin: result.pin,
            connectionType: result.connectionType || connectionType,
            wabaId: result.wabaId,
            displayPhoneNumber: result.phoneData.display_phone_number,
          }
        );

        toast.success(
          connectionType === "coexistence"
            ? "WhatsApp Business conectado. Podés usar el celular y Wembii."
            : "Whastapp sincronizado exitosamente"
        );
      } catch (error) {
        console.error(error);
        toast.error("Ha ocurrido un error al sincronizar whatsapp");
      }
    },
    [bind]
  );

  const get = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getWhatsappInitAction());
        turnOnLoading(WHATSAPP_GET_LOADING);
        const response = await whatsappApiService.get(companyId);
        dispatch(getWhatsappSuccessAction({ settings: response }));
        turnOffLoading(WHATSAPP_GET_LOADING, 1500);
      } catch (error: any) {
        turnOffLoading(WHATSAPP_GET_LOADING);
        console.error(error);
        dispatch(getWhatsappFailureAction({ error }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading]
  );

  return {
    settings,
    unbind,
    bind,
    get,
    completeAuth0Step,
  };
}
